<template>
  <v-card color="#f0f8f6" class="overflow-hidden rounded-xl">
    <div class="text-right ma-3">
      <v-btn icon @click="$emit('cancel')">
        <v-icon large>mdi-close</v-icon>
      </v-btn>
    </div>
    <v-row justify="center" align="center" class="pb-5">
      <v-col cols="16" class="text-center text-h4">
        <div>Купить курс</div>
      </v-col>
    </v-row>
    <v-card-text>
      <v-container>
        <v-row>
          <v-col cols="12">
            <div class="course-select-box">
              <div class="course-select-box__title">Выберите курс</div>
              <v-text-field
                outlined
                dense
                rounded
                v-model="searchQuery"
                prepend-inner-icon="mdi-magnify"
                label="Поиск по ID или названию"
                single-line
                hide-details
                clearable
                class="search-field mb-3"
              />
              <div v-if="searchQuery && searchQuery.trim()">
                <div v-if="searchResults.length > 0" class="search-results">
                  <div
                    v-for="result in searchResults"
                    :key="result.id"
                    class="search-result-item"
                    :class="{
                      'search-result-item--selected':
                        selectedCourse === result.id,
                    }"
                    @click="selectFromSearch(result.id)"
                  >
                    <v-icon
                      class="search-result-item__check"
                      :color="
                        selectedCourse === result.id
                          ? 'teal darken-1'
                          : 'grey lighten-1'
                      "
                    >
                      {{
                        selectedCourse === result.id
                          ? "mdi-checkbox-marked-circle"
                          : "mdi-checkbox-blank-circle-outline"
                      }}
                    </v-icon>
                    <div class="search-result-item__content">
                      <div class="search-result-item__name">
                        {{ result.name }}
                      </div>
                      <div class="search-result-item__meta">
                        ID: {{ result.id
                        }}<span v-if="result.matchedProductId">
                          · продукт ID: {{ result.matchedProductId }}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="search-empty">
                  <v-icon color="grey lighten-1" size="32"
                    >mdi-magnify-close</v-icon
                  >
                  <span>Ничего не найдено</span>
                </div>
              </div>
              <div v-else class="tree-box">
                <products-tree-view
                  @update:selectedCategory="onCourseSelected"
                  :selectedCategory="selectedCourse || null"
                />
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row v-if="hasStages">
          <v-col cols="12">
            <div class="course-select-box">
              <div class="course-select-box__title">Выберите этап курса</div>
              <select-course-stage
                :stage="selectedStage"
                :categoryId="selectedCourse"
                @confirm="onStageSelected"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions class="justify-center">
      <v-btn
        class="white--text custom-btn-filled"
        @click="buyCourse"
        :disabled="!selectedCourse || (hasStages && !selectedStage)"
      >
        Купить курс
        <v-icon right small>mdi-arrow-right</v-icon>
      </v-btn>
    </v-card-actions>

    <error-message-wrapper ref="error_wrapper" />
    <success-message-wrapper
      ref="success_wrapper"
      success-button="Купить курс"
      @ok-clicked="onSuccessOk"
      @cancel-clicked="onCancelPurchase"
    >
    </success-message-wrapper>
    <success-message-wrapper
      ref="payment_success"
      success-button="ОК"
      @ok-clicked="onPaymentSuccess"
    >
      <template #default>
        <div class="copy-field">
          <v-text-field
            v-model="courseLink"
            readonly
            label="Ссылка на курс"
            variant="outlined"
            density="compact"
          />
          <v-btn
            color="primary"
            @click="copyLink"
            class="ml-2"
            style="min-width: 100px"
          >
            Скопировать
          </v-btn>
        </div>
      </template>
    </success-message-wrapper>
    <v-snackbar v-model="showPopup" :color="popupColor" timeout="3000">
      {{ popupMessage }}
    </v-snackbar>
  </v-card>
</template>

<script>
import ProductsTreeView from "@/components/ProductsTreeView.vue";
import ErrorMessageWrapper from "@/components/ErrorMessageWrapper.vue";
import SuccessMessageWrapper from "@/components/SuccessMessageWrapper.vue";
import SelectCourseStage from "@/components/SelectCourseStage.vue";
import { mapActions, mapGetters } from "vuex";
import NSApi from "@/api/NSApi";
import { CATEGORIES } from "@/utils/constants";

export default {
  name: "BuyCourseModal",
  components: {
    ProductsTreeView,
    ErrorMessageWrapper,
    SuccessMessageWrapper,
    SelectCourseStage,
  },
  props: {
    patient: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedCourse: null,
      selectedStage: null,
      courseLink: "Empty",
      showPopup: false,
      popupMessage: "",
      popupColor: "",
      searchQuery: "",
    };
  },
  computed: {
    ...mapGetters(["user"]),
    ...mapGetters(["stagedCategories"]),
    ...mapGetters("companies", ["companies"]),
    ...mapGetters("products", ["products"]),
    flatEndCategories() {
      const result = [];
      const flatten = (nodes) => {
        for (const node of nodes) {
          if (!node.children || node.children.length === 0) {
            result.push(node);
          } else {
            flatten(node.children);
          }
        }
      };
      flatten(CATEGORIES);
      return result;
    },
    searchResults() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return [];
      const results = [];
      const seen = new Set();
      for (const cat of this.flatEndCategories) {
        if (
          cat.id.toString().includes(q) ||
          cat.name.toLowerCase().includes(q)
        ) {
          results.push({ id: cat.id, name: cat.name });
          seen.add(cat.id);
        }
      }
      for (const product of this.products || []) {
        if (
          product.id.toString().includes(q) &&
          !seen.has(product.category_id)
        ) {
          const cat = this.flatEndCategories.find(
            (c) => c.id === product.category_id
          );
          if (cat) {
            results.push({
              id: cat.id,
              name: cat.name,
              matchedProductId: product.id,
            });
            seen.add(cat.id);
          }
        }
      }
      return results;
    },
    userCompany() {
      return this.companies.find(
        (company) => company.id === this.user.company_id
      );
    },
    hasStages() {
      if (!this.selectedCourse || !this.stagedCategories) return false;
      const stagedCategory = this.stagedCategories.find(
        (stagedCategory) => stagedCategory.id === this.selectedCourse
      );
      return stagedCategory && stagedCategory.stages != null;
    },
  },
  methods: {
    ...mapActions("products", ["request_products"]),
    ...mapActions(["request_stagedCategories"]),
    selectFromSearch(categoryId) {
      this.onCourseSelected(
        this.selectedCourse === categoryId ? null : categoryId
      );
    },
    onCourseSelected(course) {
      this.selectedCourse = course;
      // Сбрасываем выбранный этап при смене курса
      this.selectedStage = null;
    },
    onStageSelected(stage) {
      this.selectedStage = stage;
    },
    buyCourse() {
      NSApi._instance
        .getOrderPrice(this.selectedCourse)
        .then((data) => {
          const priceText =
            data.price === 0
              ? "Курс будет назначен бесплатно (пробный период)."
              : `Стоимость курса составит: ${data.price}. Нажмите ОК, чтобы продолжить.`;
          this.$refs.success_wrapper.showSuccessMessage(
            "Назначение курса",
            priceText
          );
        })
        .catch((err) => {
          this.$refs.error_wrapper.showErrorMessage(
            "Ошибка",
            err.response?.data?.message || "Произошла неожиданная ошибка."
          );
        });
    },
    onSuccessOk() {
      NSApi._instance
        .doB2B_Course(this.selectedCourse, this.patient.id, this.selectedStage)
        .then((data) => {
          this.courseLink = data.link;
          return data;
        })
        .then(() => {
          this.$refs.payment_success.showSuccessMessage(
            "Курс успешно оплачен",
            ""
          );
        })
        .catch((error) => {
          this.popupMessage =
            "Произошла ошибка при проведении платежа. " +
            (error.response?.data?.message ||
              error.response?.data?.error ||
              error.message);
          this.popupColor = "error";
          this.showPopup = true;
        });
    },
    onCancelPurchase() {
      // Просто закрываем модалку, не сбрасывая выбор
    },
    onPaymentSuccess() {
      this.$emit("course-purchased");
      this.$emit("cancel");
    },
    copyLink() {
      const link = this.courseLink;
      if (!link || link === "Empty") {
        this.popupMessage =
          "Ссылка не скопирована! Курс не оплачен или не активен. Обратитесь к администратору.";
        this.popupColor = "error";
        this.showPopup = true;
        return;
      }

      navigator.clipboard
        .writeText(link)
        .then(() => {
          this.popupMessage = "Ссылка успешно скопирована!";
          this.popupColor = "success";
          this.showPopup = true;
        })
        .catch((err) => {
          this.popupMessage =
            "Ошибка копирования ссылки! Обратитесь к администратору.";
          this.popupColor = "error";
          this.showPopup = true;
          console.error(err);
        });
    },
  },
  async mounted() {
    this.request_products("sort=category_id");
    await this.request_stagedCategories();
  },
};
</script>

<style scoped lang="scss">
@import "~vuetify/src/styles/styles.sass";

.copy-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

.course-select-box {
  border: 1.5px solid #c8e6e0;
  border-radius: 16px;
  padding: 20px;
  background: #fff;

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: #2d5a52;
    margin-bottom: 14px;
  }
}

.search-field {
  ::v-deep .v-input__slot {
    background: #f0f8f6 !important;
  }
}

.search-results {
  border: 1.5px solid #e0eeeb;
  border-radius: 12px;
  overflow: hidden;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f0f8f6;
  }

  &--selected {
    background: #e6f4f1;
  }

  &__check {
    flex-shrink: 0;
  }

  &__content {
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    color: #1a1a1a;
    word-break: break-word;
    white-space: normal;
    line-height: 1.4;
  }

  &__meta {
    font-size: 11px;
    color: #9e9e9e;
    margin-top: 2px;
  }
}

.search-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  color: #bdbdbd;
  font-size: 14px;
}

.tree-box {
  border: 1.5px solid #e0eeeb;
  border-radius: 12px;
  overflow: hidden;
  background: #fafffe;
}

.custom-btn-filled {
  background-color: #d86568 !important;
  border-radius: 24px !important;
  height: 48px !important;
  padding: 0 24px !important;
  text-transform: none !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  letter-spacing: normal !important;
}

.custom-btn-filled:hover {
  background-color: #c75558 !important;
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  .v-card #tree {
    overflow-x: scroll;
  }
  .copy-field {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
